import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import Joi from 'joi';
import moment from 'moment';

import {
    useCreateSpecialDistributorMutation,
    useGetDistributorCompanyEnrichedQuery,
    useGetInvestmentFundsQuery,
    useGetPromotersQuery,
    useGetRolesQuery,
    useGetTunnelMessagesQuery,
    useUpdateSpecialDistributorMutation,
} from 'api/main';
import {
    CognitoErrors,
    COMPANY_NAME_REGEX,
    conventionStatus,
    distributorType,
    EMAIL_VALIDATION_OPTIONS,
    EURO_SYMBOL,
    InternalErrors,
    investorErrors,
    NAME_REGEX,
    ORDER_BY,
    RoleNames,
    sortDirection,
} from 'utils/constants';
import { boolToStr, checkImageFile, sortStringValueByKey, validateSchema } from 'utils/helpers';
import { corporateForms, positions, titles } from 'utils/formData';
import {
    initDistributorDocuments,
    initDistributorKYC,
    initDistributorPayload,
} from 'schemas/tunnel';
import { texts } from 'utils/languages/fr';

import { DocumentUpload, MultipleDocumentUpload } from 'containers/tunnel/common';
import {
    ActionButton,
    CancelButton,
    CustomDatePicker,
    CustomFormLabel,
    CustomPhoneField,
    CustomSelect,
    CustomTextField,
    ErrorMessage,
    FullAddressForm,
    Loader,
    RadioLabel,
    CheckboxLabel,
    TransferList,
    ListContainer,
} from 'components';
import {
    Box,
    Breadcrumbs,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    Link,
    MenuItem,
    RadioGroup,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    percentageField: { width: 80, margin: '0 10px 0 0' },
    formControlLabel: {
        cursor: 'initial',
        margin: '0 0 16px 0',
        alignItems: 'center',
    },
}));

const fileUploadProps = {
    marginTop: 0,
    width: '100%',
};

const userRoles = [RoleNames.ADMIN_CIF, RoleNames.PSI];

const renderOption = opt => (
    <MenuItem key={opt} value={opt}>
        {opt}
    </MenuItem>
);

const getDefaultForm = () => ({
    title: titles[0],
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: '',
    corporateName: '',
    corporateForm: '',
    otherCorporateForm: '',
    organization: '',
    shareCapital: '',
    function: '',
    currentAddress: {
        address: '',
        city: '',
        zipCode: '',
        country: 'FR',
    },
    legalRepresentant: '',
    representativeName: '',
    representativeRole: '',
    vatNumber: '',
    rcsNumber: '',
    rcsRegistrationAddress: '',
    rcsRegistrationDate: '',
    oriasNumber: '',
    acprNumber: '',
    distributionWays: '',
    privateEquityAssetManagementPercentage: '',
    informedClientPercentage: '',
    professionalClientPercentage: '',
    nonProfessionalClientPercentage: '',
    categoryCommercialization: '',
    assetManagementAmount: '',
    averagePrivateEquityTicketAmount: '',
    oriasAttestation: null,
    beneficiariesRegister: null,
    beneficiariesIdentityDocuments: [],
    legalRepresentativesIdentityDocuments: [],
    kbis: null,
    insuranceCertificate: null,
    legalRepresentantId: null,
    legalRepresentantAuthority: null,
    signatoryId: null,
    rib: null,
    logo: null,
    type: distributorType.GROUPEMENT,
    commissionsMenu: false,
    billingEmails: false,
    products: [],
    promoters: [],
});

const groupSchema = {
    lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
        'string.min': texts.LAST_NAME_LENGTH,
        'string.max': texts.LAST_NAME_LENGTH,
        'string.pattern.base': texts.LAST_NAME_PATTERN,
    }),
    firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
        'string.min': texts.FIRST_NAME_LENGTH,
        'string.max': texts.FIRST_NAME_LENGTH,
        'string.pattern.base': texts.FIRST_NAME_PATTERN,
    }),
    corporateName: Joi.string().required().pattern(COMPANY_NAME_REGEX).messages({
        'string.empty': texts.FIELD_REQUIRED,
        'string.pattern.base': texts.INVALID_INPUT,
    }),
    email: Joi.string().email(EMAIL_VALIDATION_OPTIONS).required().messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
    }),
    phoneNumber: Joi.string().required().messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
        'any.invalid': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
    }),
    categoryCommercialization: Joi.string()
        .required()
        .messages({ 'string.empty': texts.REMUNERATION_METHOD_ERROR }),
    conditions: Joi.object({
        termsAgreement: Joi.boolean().valid(true),
        acceptedEmails: Joi.boolean().valid(true),
    }),
    promoters: Joi.array().min(1).messages({ 'array.min': texts.AT_LEAST_ONE_PROMOTER }),
};
const standardSchema = {
    ...groupSchema,
    shareCapital: Joi.number()
        .precision(3)
        .required()
        .messages({ 'number.base': texts.SAISIR_LE_CHAMP }),
    corporateForm: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    otherCorporateForm: Joi.when('corporateForm', {
        is: Joi.string().equal(corporateForms.other),
        then: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
        otherwise: Joi.any(),
    }),
    organization: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    function: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    currentAddress: {
        address: Joi.string().required().messages({
            'any.required': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS,
            'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS,
        }),
        zipCode: Joi.string().required().messages({
            'any.required': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL,
            'string.empty': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL,
        }),
        city: Joi.string().required().messages({
            'any.required': texts.VEUILLEZ_SAISIR_LA_VILLE,
            'string.empty': texts.VEUILLEZ_SAISIR_LA_VILLE,
        }),
    },
    legalRepresentant: Joi.string().required().messages({
        'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
        'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
    }),
    representativeName: Joi.when('legalRepresentant', {
        is: Joi.string().equal('no'),
        then: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
        otherwise: Joi.any(),
    }),
    representativeRole: Joi.when('legalRepresentant', {
        is: Joi.string().equal('no'),
        then: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
        otherwise: Joi.any(),
    }),
    rcsNumber: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    rcsRegistrationAddress: Joi.string()
        .trim()
        .pattern(/^[ a-zA-ZÀ-ÿ]+$/)
        .required()
        .messages({
            'string.pattern.base': texts.RCS_REGISTRATION_ADDRESS_ONLY_LETTERS,
            'string.empty': texts.SAISIR_LE_CHAMP,
        }),
    rcsRegistrationDate: Joi.any()
        .invalid(null, '')
        .messages({ 'any.invalid': texts.FIELD_REQUIRED }),
    vatNumber: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    rib: Joi.any().invalid(null).messages({ 'any.invalid': texts.FIELD_REQUIRED }),
    kbis: Joi.any().invalid(null).messages({ 'any.invalid': texts.FIELD_REQUIRED }),
    insuranceCertificate: Joi.any().invalid(null).messages({ 'any.invalid': texts.FIELD_REQUIRED }),
    beneficiariesRegister: Joi.any()
        .invalid(null)
        .messages({ 'any.invalid': texts.FIELD_REQUIRED }),
    signatoryId: Joi.any().invalid(null).messages({ 'any.invalid': texts.FIELD_REQUIRED }),
    beneficiariesIdentityDocuments: Joi.array()
        .min(1)
        .messages({ 'array.min': texts.FIELD_REQUIRED }),
    legalRepresentativesIdentityDocuments: Joi.array()
        .min(1)
        .messages({ 'array.min': texts.FIELD_REQUIRED }),
    legalRepresentantId: Joi.when('legalRepresentant', {
        is: Joi.string().equal('no'),
        then: Joi.any().invalid(null).messages({ 'any.invalid': texts.FIELD_REQUIRED }),
        otherwise: Joi.any(),
    }),
    legalRepresentantAuthority: Joi.when('legalRepresentant', {
        is: Joi.string().equal('no'),
        then: Joi.any().invalid(null).messages({ 'any.invalid': texts.FIELD_REQUIRED }),
        otherwise: Joi.any(),
    }),
    promoters: Joi.array().min(1).messages({ 'array.min': texts.AT_LEAST_ONE_PROMOTER }),
};

const DistributorInvite = () => {
    const navigate = useNavigate();
    const classes = useStyles();
    const { id } = useParams();
    const mounted = useRef(false);

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery();
    const {
        data: { 0: distributor } = { 0: {} },
        isLoading: dataLoading,
        isError,
        isSuccess: dataLoaded,
    } = useGetDistributorCompanyEnrichedQuery(id, {
        skip: !id,
    });
    const [
        createSpecialDistributor,
        {
            isLoading: createLoader,
            isSuccess: createSuccessful,
            isError: createFailed,
            error: createError,
        },
    ] = useCreateSpecialDistributorMutation();
    const [
        updateSpecialDistributor,
        {
            isLoading: updateLoader,
            isSuccess: updateSuccessful,
            isError: updateFailed,
            error: updateError,
        },
    ] = useUpdateSpecialDistributorMutation();
    const { data: { promoters } = { promoters: [] }, promotersFetching } = useGetPromotersQuery({
        orderBy: ORDER_BY.name,
        direction: sortDirection.ASC,
    });

    const { data: messages } = useGetTunnelMessagesQuery();

    const [form, setForm] = useState(getDefaultForm());
    const [errors, setErrors] = useState({});
    const [emailExists, setEmailExists] = useState(false);

    const filteredProducts = useMemo(
        () =>
            fundList.filter(p => {
                const convention = distributor.conventions?.find(c => c.investmentFund.id === p.id);
                // only show funds that are associated with the selected promoters
                // or funds that the distributor already has conventions with
                return (
                    p.enabled &&
                    p.visible &&
                    (form.promoters.length
                        ? form.promoters.find(pro => pro.id === p.promoter.id) || convention
                        : true)
                );
            }),
        [fundList, distributor.conventions, form.promoters]
    );
    const filteredPromoters = useMemo(() => {
        const productsWithConventions = form.products
            .filter(p => p.checked && !p.replaced && !p.convention?.id)
            .map(p => p.id);
        const selectedFundsMatchSelectedPromoters = form.products.every(p => {
            let fund = fundList.find(f => f.id === p.id);
            return form.promoters.find(pro => pro.id === fund.promoter.id);
        });
        if (productsWithConventions.length > 0 && !selectedFundsMatchSelectedPromoters) {
            const promotersWithConventions = fundList
                .filter(f => productsWithConventions.includes(f.id))
                .map(f => f.promoter?.id);
            return promoters.filter(p => promotersWithConventions.includes(p.id));
        } else {
            return promoters;
        }
    }, [promoters, form.products, form.promoters, fundList]);
    const filteredRoles = useMemo(() => roles.filter(r => userRoles.includes(r.name)), [roles]);
    const isAdminCIF = useMemo(
        () => form.role === filteredRoles.find(r => r.name === RoleNames.ADMIN_CIF)?.id,
        [filteredRoles, form]
    );
    const isPSI = useMemo(
        () => form.role === filteredRoles.find(r => r.name === RoleNames.PSI)?.id,
        [filteredRoles, form]
    );

    const schema = useMemo(
        () =>
            form.type === distributorType.STANDARD
                ? {
                      ...standardSchema,
                      oriasAttestation: isAdminCIF
                          ? Joi.any()
                                .invalid(null)
                                .messages({ 'any.invalid': texts.FIELD_REQUIRED })
                          : Joi.any(),
                      oriasNumber: isAdminCIF
                          ? Joi.string().length(8).pattern(/^\d+$/).required().messages({
                                'string.empty': texts.SAISIR_LE_CHAMP,
                                'string.length': texts.ORIAS_NUMBER_LENGTH,
                                'string.pattern.base': texts.ORIAS_NUMBER_LENGTH,
                            })
                          : Joi.string().length(8).pattern(/^\d+$/).allow('').messages({
                                'string.length': texts.ORIAS_NUMBER_LENGTH,
                                'string.pattern.base': texts.ORIAS_NUMBER_LENGTH,
                            }),
                      acprNumber: isPSI
                          ? Joi.string()
                                .required()
                                .messages({ 'string.empty': texts.SAISIR_LE_CHAMP })
                          : Joi.any(),
                  }
                : {
                      ...groupSchema,
                      oriasNumber: Joi.string().length(8).pattern(/^\d+$/).allow('').messages({
                          'string.length': texts.ORIAS_NUMBER_LENGTH,
                          'string.pattern.base': texts.ORIAS_NUMBER_LENGTH,
                      }),
                  },
        [form, isAdminCIF, isPSI]
    );

    const productsWithConventions = useMemo(
        () =>
            id
                ? fundList.filter(
                      p =>
                          distributor.conventions?.find(c => c.investmentFund.id === p.id)
                              ?.status === conventionStatus.FULLY_SIGNED
                  )
                : [],
        [distributor, fundList, id]
    );

    // promoters that are associated with funds that the distributor has signed a convention for
    const permanentPromoters = useMemo(
        () =>
            distributor?.promoters?.filter(p =>
                productsWithConventions.find(pc => pc.promoter.id === p.id)
            ) ?? [],
        [distributor, productsWithConventions]
    );

    const updateFilteredProducts = useCallback(
        selectedPromoters => {
            const allowedProducts = fundList
                .filter(f => !!selectedPromoters.find(p => p.id === f.promoter?.id))
                .map(f => f.id);
            setForm(prevState => {
                const oldProducts = [...prevState.products];
                return {
                    ...prevState,
                    products: filteredProducts
                        .filter(
                            p => selectedPromoters.length === 0 || allowedProducts.includes(p.id)
                        )
                        .map(
                            p =>
                                oldProducts.find(o => o.id === p.id) ?? {
                                    id: p.id,
                                    name: p.name,
                                    checked: false,
                                    convention: null,
                                }
                        ),
                };
            });
        },
        [fundList, filteredProducts]
    );

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(prevState => ({ ...prevState, [key]: value }));
        },
        []
    );
    const handlePromotersChange = useCallback(
        selectedPromoters => {
            updateFilteredProducts(selectedPromoters);
            setForm(prevState => ({ ...prevState, promoters: selectedPromoters }));
        },
        [updateFilteredProducts]
    );
    const handlePhoneChange = useCallback(phone => {
        setForm(oldValue => ({ ...oldValue, phoneNumber: phone }));
    }, []);
    const handleCheckboxChange = useCallback(
        key => e => {
            const checked = e.target.checked;
            setForm(prevState => ({ ...prevState, [key]: checked }));
        },
        []
    );
    const handleProductChange = useCallback(
        id => e => {
            const { checked } = e.target;
            setForm(prevState => {
                let newProducts = [...prevState.products];
                let index = newProducts.findIndex(p => p.id === id);
                newProducts[index] = { ...newProducts[index], checked, convention: null };
                return { ...prevState, products: newProducts };
            });
        },
        []
    );
    const handleAddressChange = useCallback(address => {
        setForm(prevState => ({
            ...prevState,
            currentAddress: { ...prevState.currentAddress, ...address },
        }));
    }, []);
    const handleDateChange = useCallback(
        newDate => setForm(oldValue => ({ ...oldValue, rcsRegistrationDate: newDate })),
        []
    );
    const handleChangeConvention = useCallback(
        productId =>
            ({ target: { files } }) => {
                const file = files[0];
                setForm(prevState => {
                    let newProducts = [...prevState.products];
                    let index = newProducts.findIndex(p => p.id === productId);
                    newProducts[index] = {
                        ...newProducts[index],
                        convention: file,
                        replaced: !!(
                            id &&
                            (newProducts[index].conventionId || newProducts[index].replaced)
                        ),
                    };
                    return { ...prevState, products: newProducts };
                });
            },
        [id]
    );
    const handleRemoveConvention = useCallback(
        id => () => {
            setForm(prevState => {
                let newProducts = [...prevState.products];
                let index = newProducts.findIndex(p => p.id === id);
                newProducts[index] = { ...newProducts[index], convention: null, replaced: false };
                return { ...prevState, products: newProducts };
            });
        },
        []
    );

    const handleLogoChange = useCallback(({ target: { files } }) => {
        const file = files[0];
        checkImageFile(file, () => {
            setForm(prevState => ({ ...prevState, logo: file }));
        });
    }, []);
    const handleFileChange = useCallback(
        key =>
            ({ target: { files } }) => {
                setForm(prevState => ({ ...prevState, [key]: files[0] }));
                setErrors(prevState => ({ ...prevState, [key]: '' }));
            },
        []
    );
    const handleRemoveFile = useCallback(
        key => () => {
            setForm(prevState => ({ ...prevState, [key]: null }));
        },
        []
    );

    const handleMultipleFileChange = useCallback(
        key => files => {
            setForm(prevState => ({
                ...prevState,
                [key]: [...(prevState[key] || []), ...files],
            }));
            setErrors(prevState => ({ ...prevState, [key]: '' }));
        },
        []
    );

    const handleRemoveMultipleFile = useCallback(
        key => fileIndex => {
            setForm(prevState => ({
                ...prevState,
                [key]: prevState[key].filter((_, index) => index !== fileIndex),
            }));
        },
        []
    );

    const goBack = useCallback(() => {
        navigate('/distributors');
    }, [navigate]);

    const submitCallback = useCallback(
        error => {
            if (!error) {
                goBack();
                return;
            }
            const details =
                (error?.data?.detail?.split('\n') ||
                    error?.data?.['hydra:description']?.split('\n')) ??
                [];
            if (
                error?.data?.code === CognitoErrors.USERNAME_EXISTS ||
                details.includes(InternalErrors.USERNAME_EXISTS) ||
                details.includes(InternalErrors.EMAIL_EXISTS)
            ) {
                setEmailExists(true);
            }
            if (details.includes(investorErrors.INVALID_PHONE)) {
                setErrors(prevState => ({ ...prevState, phoneNumber: texts.INVALID_PHONE }));
            }
            if (details.includes(investorErrors.FIRST_NAME_ILLEGAL_CHARACTER)) {
                setErrors(prevState => ({
                    ...prevState,
                    firstName: texts.FIELD_ILLEGAL_CHARACTER,
                }));
            }
            if (details.includes(investorErrors.LAST_NAME_ILLEGAL_CHARACTER)) {
                setErrors(prevState => ({ ...prevState, lastName: texts.FIELD_ILLEGAL_CHARACTER }));
            }
        },
        [goBack]
    );

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        const proClientPer = +form.professionalClientPercentage;
        const informedClientPer = +form.informedClientPercentage;
        const nonProClientPer = +form.nonProfessionalClientPercentage;
        const allFieldsEmpty =
            proClientPer === 0 && informedClientPer === 0 && nonProClientPer === 0;
        const total = proClientPer + informedClientPer + nonProClientPer;
        const percentagesInvalid = !allFieldsEmpty && total !== 100;

        if (percentagesInvalid || !valid) {
            setErrors({
                ...newErrors,
                totalPercentage: percentagesInvalid ? texts.TOTAL_DES_POURCENTAGES : '',
            });
            return;
        } else {
            setErrors({});
        }
        if (id) {
            const deletedDocuments = [];
            const originalDocuments = initDistributorDocuments(
                distributor,
                distributor.user.subscriptions[0]?.bankingStatement
            );
            Object.keys(originalDocuments).forEach(key => {
                if (originalDocuments[key]?.id && !form[key]) {
                    deletedDocuments.push(originalDocuments[key]?.id);
                }
            });

            updateSpecialDistributor({ ...form, id: +id, deletedDocuments });
        } else {
            createSpecialDistributor({
                ...form,
                professionalClientPercentage: proClientPer,
                informedClientPercentage: informedClientPer,
                nonProfessionalClientPercentage: nonProClientPer,
            });
        }
    }, [form, id, schema, distributor, createSpecialDistributor, updateSpecialDistributor]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isError) {
            goBack();
        }
    }, [isError, goBack]);

    useEffect(() => {
        if (
            id &&
            fundList.length &&
            distributor?.user?.subscriptions[0] &&
            filteredRoles.length &&
            dataLoaded
        ) {
            // distributor data has been set (editing)
            setForm(prevState => ({
                ...prevState,
                ...initDistributorPayload(
                    { ...distributor.user, distributorCompany: distributor },
                    distributor.user?.subscriptions[0]
                ),
                ...initDistributorKYC(distributor),
                ...initDistributorDocuments(
                    distributor,
                    distributor.user?.subscriptions[0]?.bankingStatement
                ),
                billingEmails: distributor.billingEmails,
                commissionsMenu: distributor.commissionsMenu,
                corporateName: distributor.corporateName,
                logo: distributor.companyLogo,
                type: distributor.type,
                organization: boolToStr(distributor.organization),
                products: fundList
                    .filter(f => f.enabled && f.visible)
                    .map(p => {
                        const convention = distributor.conventions?.find(
                            c => c.investmentFund.id === p.id
                        );
                        return {
                            id: p.id,
                            name: p.name,
                            checked: !!convention,
                            conventionId: convention?.id,
                            convention: convention?.document
                                ? { ...convention?.document }
                                : undefined,
                        };
                    }),
                promoters: distributor.promoters || [],
            }));
        }
    }, [id, fundList, distributor, filteredRoles, dataLoaded]);

    useEffect(() => {
        if (!id && filteredProducts.length) {
            // creating a new distributor
            setForm(prevState => ({
                ...prevState,
                products: filteredProducts.map(
                    p =>
                        // keep the old convention if it exists
                        prevState.products.find(o => o.id === p.id) ?? {
                            id: p.id,
                            name: p.name,
                            checked: false,
                            convention: null,
                        }
                ),
            }));
        } else if (id && filteredProducts.length) {
            setForm(prevState => ({
                ...prevState,
                products: filteredProducts.map(p => {
                    const convention = distributor.conventions?.find(
                        c => c.investmentFund.id === p.id
                    );
                    return (
                        prevState.products.find(o => o.id === p.id) ?? {
                            id: p.id,
                            name: p.name,
                            checked: !!convention,
                            conventionId: convention?.id,
                            convention: convention?.document
                                ? { ...convention?.document }
                                : undefined,
                        }
                    );
                }),
            }));
        }
    }, [id, filteredProducts, filteredRoles, distributor.conventions]);

    useEffect(() => {
        if (createSuccessful || updateSuccessful) {
            goBack();
        }
    }, [createSuccessful, updateSuccessful, goBack]);

    useEffect(() => {
        if (createFailed) {
            submitCallback(createError);
        }
    }, [createFailed, submitCallback, createError]);

    useEffect(() => {
        if (updateFailed) {
            submitCallback(updateError);
        }
    }, [updateFailed, submitCallback, updateError]);

    useEffect(() => {
        if (filteredRoles.length) {
            setForm(prevState => ({
                ...prevState,
                role: filteredRoles[0].id,
            }));
        }
    }, [filteredRoles, id]);

    const conventionsFilledIn =
        form.products.find(p => p.checked) && // at least one product checked
        form.products.every(p => (p.checked ? p.convention : true)); // all checked products must have a convention file

    return (
        <ListContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Breadcrumbs
                    className={classes.breadcrum}
                    separator={<icons.MenuRight />}
                    aria-label="breadcrumb"
                >
                    <Link
                        component={RouterLink}
                        to="/distributors"
                        underline="hover"
                        variant="h5"
                        href="/distributors"
                    >
                        {texts.DISTRIBUTEUR_LIST_TITLE}
                    </Link>
                    <Link
                        component={RouterLink}
                        underline="hover"
                        variant="h5"
                        href={id ? `/distributors/edit/${id}` : '/distributors/create'}
                        to={id ? `/distributors/edit/${id}` : '/distributors/create'}
                    >
                        {id
                            ? texts.EDIT_DISTRIBUTOR(distributor?.corporateName)
                            : texts.DISTRIBUTOR_FAST_CREATE}
                    </Link>
                </Breadcrumbs>
            </Box>
            <Loader loading={dataLoading || fundsLoader || rolesLoader} marginTop={15}>
                <Box padding={2} margin="auto" width="420px">
                    <CustomTextField
                        value={form.corporateName}
                        error={errors.corporateName}
                        onChange={handleChange('corporateName')}
                        label={texts.DENOMINATION}
                    />
                    <CustomSelect
                        value={form.type}
                        onChange={handleChange('type')}
                        label={texts.TYPE}
                    >
                        {Object.keys(distributorType).map(k => (
                            <MenuItem key={k} value={distributorType[k]}>
                                {k}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    <CustomSelect
                        value={form.role}
                        onChange={handleChange('role')}
                        error={!!errors.role}
                        helperText={errors.role}
                        label={texts.PROFILE}
                    >
                        {filteredRoles.map(r => (
                            <MenuItem key={r.id} value={r.id}>
                                {RoleNames.ADMIN_CIF !== r.name ? r.name : texts.ADMIN_CIF_EXPANDED}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    <CustomSelect
                        value={form.corporateForm}
                        onChange={handleChange('corporateForm')}
                        label={texts.FORME_SOCIALE}
                        error={errors.corporateForm}
                    >
                        {Object.values(corporateForms).map(renderOption)}
                    </CustomSelect>
                    {form.corporateForm === corporateForms.other && (
                        <CustomTextField
                            showError
                            value={form.otherCorporateForm}
                            onChange={handleChange('otherCorporateForm')}
                            error={errors.otherCorporateForm}
                        />
                    )}
                    <FormControl component="fieldset">
                        <CustomFormLabel>{texts.ORGANISATION}</CustomFormLabel>
                        <RadioGroup
                            value={form.organization}
                            onChange={handleChange('organization')}
                        >
                            <RadioLabel value="true" label={texts.RESEAU} />
                            <RadioLabel value="false" label={texts.INDEPENDANT} />
                            <ErrorMessage gutterBottom message={errors.organization} />
                        </RadioGroup>
                    </FormControl>
                    <CustomTextField
                        label={texts.CAPITAL_SOCIAL}
                        value={form.shareCapital}
                        onChange={handleChange('shareCapital')}
                        error={errors.shareCapital}
                        endAdornment={
                            <InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>
                        }
                        formatNumber
                        showError
                    />
                    <FullAddressForm
                        noFixedWidth
                        label={texts.SIEGE_SOCIAL}
                        address={form.currentAddress}
                        onAddressChange={handleAddressChange}
                        errors={errors}
                    />
                    <CustomSelect
                        value={form.title}
                        onChange={handleChange('title')}
                        label={texts.CIVILITE}
                    >
                        {titles.map(renderOption)}
                    </CustomSelect>
                    <CustomTextField
                        value={form.firstName}
                        error={errors.firstName}
                        onChange={handleChange('firstName')}
                        label={texts.PRENOM}
                    />
                    <CustomTextField
                        value={form.lastName}
                        error={errors.lastName}
                        onChange={handleChange('lastName')}
                        label={texts.NOM}
                    />
                    <CustomTextField
                        inputProps={{
                            autoCapitalize: 'off',
                            autoComplete: 'off',
                            spellCheck: 'off',
                            autoCorrect: 'off',
                        }}
                        type="email"
                        label={texts.EMAIL}
                        value={form.email}
                        disabled={!!id}
                        onChange={handleChange('email')}
                        error={emailExists ? texts.CETTE_ADRESSE_EXISTE_DEJA : errors.email}
                    />
                    <CustomSelect
                        label={texts.POSITION}
                        value={form.function}
                        error={!!errors.function}
                        helperText={errors.function}
                        onChange={handleChange('function')}
                    >
                        {positions.map(renderOption)}
                    </CustomSelect>
                    <DocumentUpload
                        id="distributor-logo"
                        type="image"
                        label={texts.COMPANY_LOGO}
                        ContainerProps={fileUploadProps}
                        selectedFiles={form.logo ? [form.logo] : []}
                        handleFileChange={handleLogoChange}
                        removeFile={handleRemoveFile('logo')}
                    />
                    <CustomPhoneField
                        sx={{ flex: '1 1' }}
                        label={texts.TELEPHONE}
                        value={form.phoneNumber}
                        onChange={handlePhoneChange}
                        error={errors.phoneNumber}
                    />
                    <FormControl component="fieldset">
                        <CustomFormLabel>{texts.LEGAL_REPRESENTATIVE}</CustomFormLabel>
                        <RadioGroup
                            value={form.legalRepresentant}
                            onChange={handleChange('legalRepresentant')}
                        >
                            <RadioLabel value="yes" label={texts.OUI} />
                            <RadioLabel value="no" label={texts.NON} />
                            <ErrorMessage gutterBottom message={errors.legalRepresentant} />
                        </RadioGroup>
                    </FormControl>
                    {form.legalRepresentant === 'no' && (
                        <>
                            <CustomTextField
                                value={form.representativeName}
                                onChange={handleChange('representativeName')}
                                label={texts.REPRESENTATIVE_FULL_NAME}
                                error={errors.representativeName}
                                showError
                            />
                            <CustomSelect
                                label={texts.LEGAL_REPRESENTATIVE_POSITION}
                                value={form.representativeRole}
                                error={errors.representativeRole}
                                helperText={errors.representativeRole}
                                onChange={handleChange('representativeRole')}
                            >
                                {positions.map(renderOption)}
                            </CustomSelect>
                        </>
                    )}
                    <CustomTextField
                        label={texts.SIREN_NUMBER}
                        value={form.rcsNumber}
                        onChange={handleChange('rcsNumber')}
                        error={errors.rcsNumber}
                        showError
                    />
                    <CustomTextField
                        label={texts.RCS_ADDRESS}
                        value={form.rcsRegistrationAddress}
                        onChange={handleChange('rcsRegistrationAddress')}
                        error={errors.rcsRegistrationAddress}
                        showError
                    />
                    <CustomDatePicker
                        maxDate={moment().subtract(1, 'd').startOf('d')}
                        label={texts.RCS_DATE}
                        value={form.rcsRegistrationDate}
                        onChange={handleDateChange}
                        error={errors.rcsRegistrationDate}
                        showError
                    />
                    <CustomTextField
                        label={texts.VAT_NUMBER}
                        value={form.vatNumber}
                        onChange={handleChange('vatNumber')}
                        error={errors.vatNumber}
                        showError
                    />
                    {+form.role === filteredRoles.find(r => r.name === RoleNames.ADMIN_CIF)?.id && (
                        <CustomTextField
                            type="number"
                            label={texts.ORIAS_NUMBER}
                            value={form.oriasNumber}
                            onChange={handleChange('oriasNumber')}
                            error={errors.oriasNumber}
                            showError
                        />
                    )}
                    {+form.role === filteredRoles.find(r => r.name === RoleNames.PSI)?.id && (
                        <CustomTextField
                            label={texts.ACPR_NUMBER}
                            value={form.acprNumber}
                            error={errors.acprNumber}
                            onChange={handleChange('acprNumber')}
                        />
                    )}
                    <FormControl component="fieldset">
                        <RadioGroup
                            className={classes.flexContainer}
                            value={form.categoryCommercialization}
                            onChange={handleChange('categoryCommercialization')}
                        >
                            <CustomFormLabel>{texts.REMUNERATION_METHOD}</CustomFormLabel>
                            <RadioLabel
                                value="public"
                                label={texts.ACCEPT_REMUNERATION}
                                labelPlacement="end"
                            />
                            <RadioLabel
                                value="private"
                                label={texts.REJECT_REMUNERATION}
                                labelPlacement="end"
                            />
                            <RadioLabel value="both" label={texts.BOTH} labelPlacement="end" />
                        </RadioGroup>
                        <ErrorMessage message={errors.categoryCommercialization} />
                    </FormControl>
                    <FormControl component="fieldset">
                        <CustomFormLabel>{texts.ASSET_MANAGEMENT_AMOUNT}</CustomFormLabel>
                        <RadioGroup
                            name="assetManagementAmount"
                            value={form.assetManagementAmount}
                            onChange={handleChange('assetManagementAmount')}
                        >
                            <RadioLabel
                                value="asset_management_amount_options.between_0_and_50ME"
                                label={
                                    messages?.['asset_management_amount_options.between_0_and_50ME']
                                }
                                checked={
                                    form.assetManagementAmount ===
                                    'asset_management_amount_options.between_0_and_50ME'
                                }
                            />
                            <RadioLabel
                                value="asset_management_amount_options.between_50_and_100ME"
                                label={
                                    messages?.[
                                        'asset_management_amount_options.between_50_and_100ME'
                                    ]
                                }
                                checked={
                                    form.assetManagementAmount ===
                                    'asset_management_amount_options.between_50_and_100ME'
                                }
                            />
                            <RadioLabel
                                value="asset_management_amount_options.more_than_100ME"
                                label={
                                    messages?.['asset_management_amount_options.more_than_100ME']
                                }
                                checked={
                                    form.assetManagementAmount ===
                                    'asset_management_amount_options.more_than_100ME'
                                }
                            />
                        </RadioGroup>
                    </FormControl>
                    <CustomTextField
                        formatNumber
                        value={form.privateEquityAssetManagementPercentage}
                        onChange={handleChange('privateEquityAssetManagementPercentage')}
                        error={errors.privateEquityAssetManagementPercentage}
                        showError={!!errors.privateEquityAssetManagementPercentage}
                        label={texts.PRIVATE_EQUITY_ASSET_MANAGEMENT_AMOUNT}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                    <FormGroup className={classes.flexContainer}>
                        <CustomFormLabel>{texts.CLIENT_REPARTITION}</CustomFormLabel>
                        <FormControlLabel
                            className={classes.formControlLabel}
                            control={
                                <CustomTextField
                                    formatNumber
                                    value={form.professionalClientPercentage}
                                    inputStyle={classes.percentageField}
                                    onChange={handleChange('professionalClientPercentage')}
                                    error={errors.professionalClientPercentage}
                                    showError={!!errors.professionalClientPercentage}
                                    endAdornment={
                                        <InputAdornment position="start">%</InputAdornment>
                                    }
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" flexWrap="nowrap">
                                    {texts.PRO_CLIENTS_PERCENTAGE}
                                </Box>
                            }
                        />
                        <FormControlLabel
                            className={classes.formControlLabel}
                            control={
                                <CustomTextField
                                    formatNumber
                                    value={form.informedClientPercentage}
                                    inputStyle={classes.percentageField}
                                    onChange={handleChange('informedClientPercentage')}
                                    error={errors.informedClientPercentage}
                                    showError={!!errors.informedClientPercentage}
                                    endAdornment={
                                        <InputAdornment position="start">%</InputAdornment>
                                    }
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" flexWrap="nowrap">
                                    {texts.INFORMED_CLIENTS_PERCENTAGE}
                                </Box>
                            }
                        />
                        <FormControlLabel
                            className={classes.formControlLabel}
                            control={
                                <CustomTextField
                                    formatNumber
                                    value={form.nonProfessionalClientPercentage}
                                    inputStyle={classes.percentageField}
                                    onChange={handleChange('nonProfessionalClientPercentage')}
                                    error={errors.informedClientPercentage}
                                    showError={!!errors.informedClientPercentage}
                                    endAdornment={
                                        <InputAdornment position="start">%</InputAdornment>
                                    }
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" flexWrap="nowrap">
                                    {texts.NON_PRO_CLIENTS_PERCENTAGE}
                                </Box>
                            }
                        />
                        <ErrorMessage message={errors.totalPercentage} />
                    </FormGroup>
                    <CustomTextField
                        multiline
                        rows={3}
                        className={classes.textarea}
                        label={texts.DISTRIBUTION_WAYS}
                        value={form.distributionWays}
                        onChange={handleChange('distributionWays')}
                        error={errors.distributionWays}
                    />
                    <CustomTextField
                        label={texts.AVERAGE_PRIVATE_EQUITY_TICKET_AMOUNT}
                        value={form.averagePrivateEquityTicketAmount}
                        onChange={handleChange('averagePrivateEquityTicketAmount')}
                        error={errors.averagePrivateEquityTicketAmount}
                        endAdornment={
                            <InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>
                        }
                        showError
                        formatNumber
                    />
                    <DocumentUpload
                        id="orias-doc"
                        ContainerProps={fileUploadProps}
                        selectedFiles={[form.oriasAttestation]}
                        label={texts.ORIAS_ATTESTATION}
                        error={errors.oriasAttestation}
                        handleFileChange={handleFileChange('oriasAttestation')}
                        removeFile={handleRemoveFile('oriasAttestation')}
                    />
                    <DocumentUpload
                        id="beneficiaries-register-doc"
                        label={texts.BENEFICIARIES_REGISTER}
                        ContainerProps={fileUploadProps}
                        selectedFiles={[form.beneficiariesRegister]}
                        error={errors.beneficiariesRegister}
                        handleFileChange={handleFileChange('beneficiariesRegister')}
                        removeFile={handleRemoveFile('beneficiariesRegister')}
                    />
                    <DocumentUpload
                        id="kbis-doc"
                        ContainerProps={fileUploadProps}
                        label={texts.KBIS}
                        error={errors.kbis}
                        selectedFiles={[form.kbis]}
                        handleFileChange={handleFileChange('kbis')}
                        removeFile={handleRemoveFile('kbis')}
                    />
                    <DocumentUpload
                        id="insurance-doc"
                        ContainerProps={fileUploadProps}
                        label={texts.INSURANCE_CERTIFICATE}
                        error={errors.insuranceCertificate}
                        selectedFiles={[form.insuranceCertificate]}
                        handleFileChange={handleFileChange('insuranceCertificate')}
                        removeFile={handleRemoveFile('insuranceCertificate')}
                    />
                    {form?.legalRepresentant === 'no' && (
                        <DocumentUpload
                            id="legal-representative-id"
                            ContainerProps={fileUploadProps}
                            error={errors.legalRepresentantId}
                            label={texts.LEGAL_REPRESENTANT_ID}
                            selectedFiles={[form.legalRepresentantId]}
                            handleFileChange={handleFileChange('legalRepresentantId')}
                            removeFile={handleRemoveFile('legalRepresentantId')}
                        />
                    )}
                    {form?.legalRepresentant === 'no' && (
                        <DocumentUpload
                            id="legal-representative-mandate"
                            ContainerProps={fileUploadProps}
                            error={errors.legalRepresentantAuthority}
                            label={texts.LEGAL_REPRESENTANT_AUTHORITY_ALT}
                            selectedFiles={[form.legalRepresentantAuthority]}
                            handleFileChange={handleFileChange('legalRepresentantAuthority')}
                            removeFile={handleRemoveFile('legalRepresentantAuthority')}
                        />
                    )}
                    <DocumentUpload
                        id="signatory-id-doc"
                        ContainerProps={fileUploadProps}
                        label={texts.SIGNATORY_ID}
                        error={errors.signatoryId}
                        selectedFiles={[form.signatoryId]}
                        handleFileChange={handleFileChange('signatoryId')}
                        removeFile={handleRemoveFile('signatoryId')}
                    />
                    <MultipleDocumentUpload
                        id="beneficiaries-identity-docs"
                        type="document"
                        ContainerProps={fileUploadProps}
                        label={texts.BENEFICIARIES_IDS}
                        error={errors.beneficiariesIdentityDocuments}
                        selectedFiles={form.beneficiariesIdentityDocuments}
                        handleFileChange={handleMultipleFileChange(
                            'beneficiariesIdentityDocuments'
                        )}
                        removeFile={handleRemoveMultipleFile('beneficiariesIdentityDocuments')}
                    />
                    <MultipleDocumentUpload
                        id="legal-representatives-identity-docs"
                        type="document"
                        ContainerProps={fileUploadProps}
                        label={texts.LEGAL_REPRESENTATIVES_IDS}
                        error={errors.legalRepresentativesIdentityDocuments}
                        selectedFiles={form.legalRepresentativesIdentityDocuments}
                        handleFileChange={handleMultipleFileChange(
                            'legalRepresentativesIdentityDocuments'
                        )}
                        removeFile={handleRemoveMultipleFile(
                            'legalRepresentativesIdentityDocuments'
                        )}
                    />
                    <DocumentUpload
                        id="rib-doc"
                        label={texts.RIB_SOCIETE}
                        error={errors.rib}
                        ContainerProps={fileUploadProps}
                        selectedFiles={[form.rib]}
                        handleFileChange={handleFileChange('rib')}
                        removeFile={handleRemoveFile('rib')}
                    />
                </Box>
                <TransferList
                    label={texts.PROMOTERS}
                    permanentItems={permanentPromoters}
                    sorterFunction={sortStringValueByKey('name')}
                    renderText={p => p.name}
                    items={filteredPromoters}
                    value={form.promoters}
                    onChange={handlePromotersChange}
                    error={errors.promoters}
                    loading={promotersFetching}
                />
                <Box padding={2} margin="auto" width="420px">
                    {form.type === distributorType.GROUPEMENT && (
                        <React.Fragment>
                            <CustomFormLabel>{texts.PRODUITS}</CustomFormLabel>
                            {form.products.map(p => (
                                <Box key={p.id} mb={2} pl={1}>
                                    <CheckboxLabel
                                        label={filteredProducts.find(f => f.id === p.id)?.name}
                                        checked={p.checked}
                                        disabled={!!id && (!!p.replaced || !!p.convention?.id)}
                                        onChange={handleProductChange(p.id)}
                                    />
                                    {p.checked && (
                                        <DocumentUpload
                                            type="pdf"
                                            id={`product-convention-${p.id}`}
                                            ContainerProps={fileUploadProps}
                                            selectedFiles={[p.convention] ? [p.convention] : []}
                                            handleFileChange={handleChangeConvention(p.id)}
                                            removeFile={handleRemoveConvention(p.id)}
                                        />
                                    )}
                                </Box>
                            ))}
                            <CheckboxLabel
                                label={texts.SEES_COMMISSIONS_MENU}
                                checked={form.commissionsMenu}
                                onChange={handleCheckboxChange('commissionsMenu')}
                            />
                            <CheckboxLabel
                                label={texts.GETS_QUARTER_STATEMENTS}
                                checked={form.billingEmails}
                                onChange={handleCheckboxChange('billingEmails')}
                            />
                        </React.Fragment>
                    )}
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
                    <CancelButton onClick={goBack}>{texts.CANCEL}</CancelButton>
                    <ActionButton
                        loading={createLoader || updateLoader}
                        disabled={
                            createLoader ||
                            updateLoader ||
                            (form.type === distributorType.GROUPEMENT && !conventionsFilledIn)
                        }
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        {id ? texts.SAVE : texts.CREATE}
                    </ActionButton>
                </Box>
            </Loader>
        </ListContainer>
    );
};

export default DistributorInvite;
